<script setup>
import { ref, computed, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useImageZoom } from 'dashboard/composables/useImageZoom';
import { downloadFile } from '@chatwoot/utils';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  allAttachments: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  IG_REEL: 'ig_reel',
  AUDIO: 'audio',
};

// SZ_GALLERY_REDESIGN_20260528: janela de bolinhas (Figma mostra 8 uniformes).
const DOT_WINDOW = 8;

const isDownloading = ref(false);
const activeAttachment = ref({});
const activeFileType = ref('');
const activeImageIndex = ref(
  Math.max(
    0,
    props.allAttachments.findIndex(
      attachment => attachment.message_id === props.attachment.message_id
    )
  )
);

const imageRef = useTemplateRef('imageRef');

const {
  imageWrapperStyle,
  imageStyle,
  activeImageRotation,
  onDoubleClickZoomImage,
  onWheelImageZoom,
  onMouseMove,
  onMouseLeave,
  resetZoomAndRotation,
} = useImageZoom(imageRef);

const hasMoreThanOneAttachment = computed(
  () => props.allAttachments.length > 1
);

const isImage = computed(
  () => activeFileType.value === ALLOWED_FILE_TYPES.IMAGE
);
const isVideo = computed(() =>
  [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
    activeFileType.value
  )
);
const isAudio = computed(
  () => activeFileType.value === ALLOWED_FILE_TYPES.AUDIO
);

const fileNameFromDataUrl = computed(() => {
  const { data_url: dataUrl } = activeAttachment.value;
  if (!dataUrl) return '';

  const fileName = dataUrl.split('/').pop();
  return fileName ? decodeURIComponent(fileName) : '';
});

// Bolinhas: janela centrada no item ativo + fade nas bordas quando há mais.
const dotState = computed(() => {
  const total = props.allAttachments.length;
  const active = activeImageIndex.value;

  if (total <= DOT_WINDOW) {
    return {
      dots: Array.from({ length: total }, (_, i) => ({ i, active: i === active })),
      fadeLeft: false,
      fadeRight: false,
    };
  }

  const start = Math.min(
    Math.max(0, active - Math.floor(DOT_WINDOW / 2)),
    total - DOT_WINDOW
  );
  return {
    dots: Array.from({ length: DOT_WINDOW }, (_, k) => ({
      i: start + k,
      active: start + k === active,
    })),
    fadeLeft: start > 0,
    fadeRight: start + DOT_WINDOW < total,
  };
});

const dotsMaskStyle = computed(() => {
  const { fadeLeft, fadeRight } = dotState.value;
  if (!fadeLeft && !fadeRight) return {};
  const l = fadeLeft ? 'transparent 0' : 'black 0';
  const r = fadeRight ? 'transparent 100%' : 'black 100%';
  const grad = `linear-gradient(to right, ${l}, black 14px, black calc(100% - 14px), ${r})`;
  return { maskImage: grad, WebkitMaskImage: grad };
});

const onClose = () => emit('close');

const setImageAndVideoSrc = attachment => {
  const { file_type: type } = attachment;
  if (!Object.values(ALLOWED_FILE_TYPES).includes(type)) return;

  activeAttachment.value = attachment;
  activeFileType.value = type;
};

const onClickChangeAttachment = (attachment, index) => {
  if (!attachment) return;

  activeImageIndex.value = index;
  setImageAndVideoSrc(attachment);
  resetZoomAndRotation();
};

const goPrev = () =>
  onClickChangeAttachment(
    props.allAttachments[activeImageIndex.value - 1],
    activeImageIndex.value - 1
  );
const goNext = () =>
  onClickChangeAttachment(
    props.allAttachments[activeImageIndex.value + 1],
    activeImageIndex.value + 1
  );

const onClickDownload = async () => {
  const { file_type: type, data_url: url, extension } = activeAttachment.value;
  if (!Object.values(ALLOWED_FILE_TYPES).includes(type)) return;

  try {
    isDownloading.value = true;
    await downloadFile({ url, type, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const keyboardEvents = {
  Escape: { action: onClose },
  ArrowLeft: { action: goPrev },
  ArrowRight: { action: goNext },
};

useKeyboardEvents(keyboardEvents);

onMounted(() => {
  setImageAndVideoSrc(props.attachment);
});
</script>

<template>
  <TeleportWithDirection to="body">
    <woot-modal
      v-model:show="show"
      :show-close-button="false"
      :on-close="onClose"
    >
      <!-- SZ_GALLERY_REDESIGN_20260528 — card centralizado (Figma 1468x990) -->
      <div
        class="sz-gallery-root flex flex-col w-[90vw] max-w-[880px] h-[85vh] max-h-[760px] overflow-hidden rounded-xl bg-n-background select-none"
      >
        <!-- Topo: nome do arquivo + X -->
        <header
          class="flex items-center justify-between shrink-0 h-[60px] px-4 bg-n-background border-b border-n-weak"
        >
          <span
            v-dompurify-html="fileNameFromDataUrl"
            class="min-w-0 flex-1 truncate text-xl font-medium text-n-slate-12"
          />
          <NextButton
            icon="i-lucide-x"
            slate
            ghost
            class="ml-2 shrink-0"
            @click="onClose"
          />
        </header>

        <main
          class="relative flex items-stretch flex-1 h-full overflow-hidden"
        >
          <!-- passador anterior — faixa com gradiente (Figma), aparece só ao
               aproximar o mouse desse lado -->
          <button
            v-if="hasMoreThanOneAttachment"
            type="button"
            class="sz-gv-pager sz-gv-pager--left"
            :disabled="activeImageIndex === 0"
            aria-label="Anterior"
            @click="goPrev"
          >
            <Icon icon="i-lucide-chevron-left" class="size-8" />
          </button>

          <div
            class="flex-1 flex items-center justify-center overflow-hidden p-4"
          >
            <div
              v-if="isImage"
              :style="imageWrapperStyle"
              class="flex items-center justify-center origin-center"
              :class="{
                'w-[calc(100dvh-8rem)] h-[calc(100dvw-7rem)]':
                  activeImageRotation % 180 !== 0,
                'size-full': activeImageRotation % 180 === 0,
              }"
            >
              <img
                ref="imageRef"
                :key="activeAttachment.message_id"
                :src="activeAttachment.data_url"
                :style="imageStyle"
                class="max-h-full max-w-full object-contain duration-100 ease-in-out transform select-none rounded-lg border border-[#D4D4D4]"
                @dblclick="onDoubleClickZoomImage"
                @wheel.prevent="onWheelImageZoom"
                @mousemove="onMouseMove"
                @mouseleave="onMouseLeave"
              />
            </div>

            <video
              v-if="isVideo"
              :key="activeAttachment.message_id"
              :src="activeAttachment.data_url"
              controls
              playsInline
              class="max-h-full max-w-full object-contain rounded-lg"
            />

            <audio
              v-if="isAudio"
              :key="activeAttachment.message_id"
              controls
              class="w-full max-w-md"
            >
              <source :src="`${activeAttachment.data_url}?t=${Date.now()}`" />
            </audio>
          </div>

          <!-- passador próximo — faixa com gradiente espelhada -->
          <button
            v-if="hasMoreThanOneAttachment"
            type="button"
            class="sz-gv-pager sz-gv-pager--right"
            :disabled="activeImageIndex === allAttachments.length - 1"
            aria-label="Próxima"
            @click="goNext"
          >
            <Icon icon="i-lucide-chevron-right" class="size-8" />
          </button>
        </main>

        <!-- Rodapé: contador + bolinhas (centro) + Salvar (direita) -->
        <footer
          class="relative flex items-center justify-center shrink-0 h-16 px-3 bg-n-background border-t border-n-weak"
        >
          <div class="flex items-center gap-2">
            <span class="text-sm font-normal text-n-slate-12 tabular-nums">
              {{ activeImageIndex + 1 }}/{{ allAttachments.length }}
            </span>
            <div
              class="flex items-center gap-2"
              :style="dotsMaskStyle"
            >
              <span
                v-for="dot in dotState.dots"
                :key="dot.i"
                class="w-3 h-3 rounded-full transition-colors duration-150"
                :class="dot.active ? 'bg-[#388E3C]' : 'bg-[#E5E5E5]'"
              />
            </div>
          </div>

          <button
            type="button"
            class="absolute right-3 inline-flex items-center h-10 gap-2 pl-4 pr-3 text-sm font-semibold transition-colors border-[1.5px] rounded-lg border-[#D4D4D4] bg-n-background text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-50"
            :disabled="isDownloading"
            @click="onClickDownload"
          >
            Salvar
            <Icon icon="i-lucide-download" class="size-5" />
          </button>
        </footer>
      </div>
    </woot-modal>
  </TeleportWithDirection>
</template>

<style scoped>
/* SZ_GALLERY_PAGER_20260529 — passador = faixa com gradiente (Figma).
   Escuro na borda → transparente pro centro. Estado padrão invisível;
   aparece (fade) só ao aproximar o mouse daquele lado. */
.sz-gv-pager {
  position: absolute;
  top: 0;
  height: 100%;
  width: 240px;
  max-width: 38%;
  z-index: 10;
  display: flex;
  align-items: center;
  color: #ffffff;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.sz-gv-pager:hover {
  opacity: 1;
}
.sz-gv-pager:disabled {
  opacity: 0 !important;
  pointer-events: none;
}
.sz-gv-pager--left {
  left: 0;
  justify-content: flex-start;
  padding-left: 1.5rem;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
}
.sz-gv-pager--right {
  right: 0;
  justify-content: flex-end;
  padding-right: 1.5rem;
  background: linear-gradient(
    to left,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
}
</style>

<style>
/* SZ_GALLERY_ZINDEX_20260528: o SzTopBar usa z-index 10000, acima do
   .modal-mask (z-9990) do woot-modal — por isso o lightbox ficava atrás do
   topbar. Sobe só a máscara que contém o gallery acima de tudo. */
.modal-mask:has(.sz-gallery-root) {
  z-index: 100000 !important;
}
</style>
